import * as React from "react";

import { cn } from "utils/cn";
import { formatDateTime, formatRelative, toDate } from "utils/format";

// Matches the server's STALE_AFTER_MS (services/teleopdLink/linkStatus.js).
const STALE_AFTER_MS = 60 * 60 * 1000;

export const isSyncStale = (lastSyncAt: string | null | undefined, now = Date.now()): boolean => {
  const date = toDate(lastSyncAt);
  return !date || now - date.getTime() > STALE_AFTER_MS;
};

interface TeleopdLinkBadgeProps {
  linked: boolean;
  lastSyncAt: string | null | undefined;
  /** Server-computed staleness when known; otherwise derived from lastSyncAt. */
  stale?: boolean;
  enabled?: boolean;
}

/** "Connected · 3 min ago" / "Stale · 2 h ago" / "Not connected". */
const TeleopdLinkBadge: React.FC<TeleopdLinkBadgeProps> = ({
  linked,
  lastSyncAt,
  stale,
  enabled = true,
}) => {
  if (!linked) {
    return (
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-medium text-muted-foreground">
        <span className="h-2 w-2 rounded-full bg-input" aria-hidden="true" />
        Not connected
      </span>
    );
  }
  if (!enabled) {
    return (
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-medium text-muted-foreground">
        <span className="h-2 w-2 rounded-full bg-subtle-foreground" aria-hidden="true" />
        Disabled
      </span>
    );
  }
  const isStale = stale === undefined ? isSyncStale(lastSyncAt) : stale;
  const age = lastSyncAt ? formatRelative(lastSyncAt) : "never synced";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-medium",
        isStale ? "text-warning-text" : "text-success-text"
      )}
      title={lastSyncAt ? `Last sync ${formatDateTime(lastSyncAt)}` : "TeleOpd has not synced yet"}
    >
      <span
        className={cn("h-2 w-2 rounded-full", isStale ? "bg-warning" : "bg-success")}
        aria-hidden="true"
      />
      {isStale ? "Stale" : "Connected"} · {age}
    </span>
  );
};

export default TeleopdLinkBadge;
